import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of, Subject } from 'rxjs';
import { isRenderableSignal, KpiData, SignalCatalog, SignalsApiRequest, Widget } from '../models/widget-models';
import { COMPOSITE_KPI_DATA, KPI_CUSTOM_DATA, KPI_DATA, SIGNALS_FUNCTION } from 'src/app/core/config/config.constants';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class KpiService {

  private baseUrl: string = environment.baseTsOhwUrl;

  onChangeDataDisplayed$: Subject<any> = new Subject<any>();

  constructor(private _httpClient: HttpClient) { }

  getKpiData(command: SignalsApiRequest): Observable<any> {
    const apiCommand = {
      uidCustomer: command.customerId,
      signals: command.signals,
      fromDate: command.datesRange[0],
      toDate: command.datesRange[1],
      timeZone: "utc",
      aggregate: command.aggregateData ?? true,
      uidVehicles: command.machineIds
    };
    return this._httpClient.post(`${this.baseUrl}${KPI_DATA}`, apiCommand);
  }

  getKpiCustomData(command: SignalsApiRequest): Observable<any> {
    const apiCommand = {
      uidCustomer: command.customerId,
      uidsKpi: command.signals,
      fromDate: command.datesRange[0],
      toDate: command.datesRange[1],
      timeZone: "utc",
      aggregate: command.aggregateData ?? true,
      uidVehicles: command.machineIds
    };
    return this._httpClient.post(`${this.baseUrl}${KPI_CUSTOM_DATA}`, apiCommand);
  }

  getKpiVehicleStats(command: SignalsApiRequest): Observable<any> {
    const apiCommand = {
      uidCustomer: command.customerId,
      signals: command.signals,
      fromDate: command.datesRange[0],
      toDate: command.datesRange[1],
      timeZone: "utc",
      aggregate: command.aggregateData ?? true,
      uidVehicles: command.machineIds
    };

    return this._httpClient.post(`${this.baseUrl}${COMPOSITE_KPI_DATA}`, apiCommand);
  }

  getUniqueKpis(widgets: Widget[]): { key: string, scope: string }[] {
    return Array.from(
      new Set(
        widgets.map(widget => {
            const signalType: SignalCatalog[] | SignalCatalog = cloneDeep(widget.config?.specific?.signalType);            
            if (Array.isArray(signalType)) {
              signalType.forEach(s => {
                // Il backend risolve i kpi_custom per uid, tutti gli altri scope per name
                s.apiKey = s.scope === 'kpi_custom' ?
                  { key: s.uid, scope: s.scope } : { key: s.name, scope: s.scope };
              });
            return signalType.map((item: any) => item?.apiKey ?? item);
            } else if (signalType != null && typeof signalType === 'object' && 'apiKey' in signalType) {
            return [signalType.apiKey];
            } else if (signalType != null) {
            return [signalType];
            }
            return [];
        })
        .reduce((acc, val) => acc.concat(val), [])
      )
    );
  }

  /**
   * Filters an array of KPI objects, returning only those whose result_signal matches expectedKpis.
   * @param data Array of KPI objects with a result_signal property
   * @param expectedKpis Array of expected KPI names
   * @returns Filtered array of KPI objects
   */
  /**
   * Converts a decimal hour value to HH:mm string.
   */
  private decimalToHHmm(decimal: number): string {
    if (typeof decimal !== 'number' || isNaN(decimal)) return '';
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * Costruisce le righe KPI a partire dai KPI attesi (selezionati nel widget) e dai dati API.
   * Tile KPI (isPie=false): una riga per OGNI KPI selezionato — se l'API non restituisce il dato
   *   (es. data: []) si crea un placeholder senza valore, così il template mostra "N / A".
   * Pie (isPie=true): comportamento invariato — solo i KPI che hanno effettivamente un dato.
   * Se 'hour'/'time' è nel nome del segnale, result_value/result_avg_value sono formattati come HH:mm.
   */
  extractExpectedKpi(data: any[], expectedKpis: SignalCatalog[], isPie: boolean = false): KpiData[] {
    if (!Array.isArray(expectedKpis)) return [];
    const rows: any[] = Array.isArray(data) ? data : [];

    // Esclude i segnali disabilitati/eliminati dal catalogo: il view-container è un risultato,
    // non va mostrato (niente badge, niente "N / A" al loro posto). Solo presentazionale:
    // expectedKpis (quindi widget.config.specific.signalType) non viene toccato.
    const renderableKpis = expectedKpis.filter(isRenderableSignal);

    // result_signal è la chiave inviata in getUniqueKpis: uid per i kpi_custom, name per gli altri scope
    const matchesKpi = (item: any, kpi: SignalCatalog) => kpi?.uid === item.result_signal || kpi?.name === item.result_signal;

    // NOTA: il nome leggibile del segnale (per l'euristica hour/time) va preso da kpi.name:
    // per i kpi_custom result_signal è l'uid, non il name.
    const isTimeSignal = (kpi: SignalCatalog | undefined): boolean => {
      const signalName = (kpi?.name ?? '').toLowerCase();
      return signalName.includes('hour') || signalName.includes('time');
    };

    const mapItem = (item: any, kpi: SignalCatalog | undefined): any => {
      const newItem = { ...item };
      newItem.i18nLabel = kpi?.i18nLabel;
      newItem.name = kpi?.label ?? item.result_signal;
      newItem.isTimeValue = isTimeSignal(kpi);

      if (!isPie && newItem.isTimeValue) {
        const val = typeof newItem.result_value === 'string' ? parseFloat(newItem.result_value) : newItem.result_value;
        const avgVal = typeof newItem.result_avg_value === 'string' ? parseFloat(newItem.result_avg_value) : newItem.result_avg_value;
        if (!isNaN(val)) newItem.result_value = this.decimalToHHmm(val);
        if (!isNaN(avgVal)) newItem.result_avg_value = this.decimalToHHmm(avgVal);
      }
      return newItem;
    };

    // Pie: solo i KPI con un dato reale (una fetta "N/A" non avrebbe senso in un grafico)
    if (isPie) {
      return rows
        .filter(item => renderableKpis.some(kpi => matchesKpi(item, kpi)))
        .map(item => mapItem(item, renderableKpis.find(kpi => matchesKpi(item, kpi))));
    }

    // Tile KPI: una riga per ogni KPI selezionato, con placeholder (→ "N / A") se manca il dato
    return renderableKpis
      .filter(kpi => !!kpi)
      .map(kpi => {
        const item = rows.find(it => matchesKpi(it, kpi));
        if (item) return mapItem(item, kpi);
        return {
          result_signal: kpi.uid ?? kpi.name ?? '',
          result_value: null,
          result_avg_value: null,
          name: kpi.label ?? kpi.name,
          i18nLabel: kpi.i18nLabel,
          isTimeValue: isTimeSignal(kpi)
        } as any;
      });
  }

  getKpiByIdentifier(identifier: string): Observable<any> {
    return of([]);
  }
}